import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { transactionAPI } from '../services/api';
import { Search, Plus, Edit, Trash2, FileText } from 'lucide-react';
import { formatDate } from '../utils/formatters';
import NumberInput from '../components/NumberInput';

// 민원 종류 상수
const COMPLAINT_TYPES = [
  '각자',
  '개장',
  '개장 및 반출',
  '관리비',
  '구매',
  '리모델링',
  '묘역위치',
  '사용문의',
  '신단지 문의',
  '연고자 정보 변경 및 등록',
  '장례',
  '해약',
  '기타'
];

// 민원 접수 유형 상수
const RECEPTION_TYPES = ['전화', '방문'];

const Maintenance = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  
  const [formData, setFormData] = useState({
    transaction_id: '',
    maintenance_type: '기타',
    content: '',
    maintenance_date: new Date().toISOString().split('T')[0],
    cost: '',
    status: 'pending',
    applicant_name: '',
    applicant_phone: '',
    manager: '',
    reception_type: '전화',
    memo: ''
  });

  // 민원 목록 조회
  const { data: maintenance, isLoading } = useQuery({
    queryKey: ['maintenance', currentPage, searchTerm, selectedStatus, selectedType],
    queryFn: () => transactionAPI.getMaintenance({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      status: selectedStatus,
      maintenance_type: selectedType
    }),
    keepPreviousData: true,
    select: (data) => {
      if (data?.data?.maintenance) {
        return {
          data: data.data.maintenance,
          pagination: data.data.pagination
        };
      } else if (data?.data) {
        return {
          data: Array.isArray(data.data) ? data.data : [],
          pagination: data.pagination
        };
      } else if (Array.isArray(data)) {
        return {
          data: data,
          pagination: null
        };
      }
      return {
        data: [],
        pagination: null
      };
    }
  });

  // 민원 추가/수정 뮤테이션
  const mutation = useMutation({
    mutationFn: (data) => {
      if (editingMaintenance) {
        return transactionAPI.updateMaintenance(editingMaintenance.id, data);
      } else {
        return transactionAPI.createMaintenance(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['maintenance']);
      showToast(editingMaintenance ? '민원이 수정되었습니다.' : '민원이 등록되었습니다.', 'success');
      setShowAddModal(false);
      setEditingMaintenance(null);
      resetForm();
    },
    onError: (error) => {
      showToast(error.message || '오류가 발생했습니다.', 'error');
    }
  });

  const resetForm = () => {
    setFormData({
      transaction_id: '',
      maintenance_type: '기타',
      content: '',
      maintenance_date: new Date().toISOString().split('T')[0],
      cost: '',
      status: 'pending',
      applicant_name: '',
      applicant_phone: '',
      manager: '',
      reception_type: '전화',
      memo: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  const handleEdit = (maintenance) => {
    setEditingMaintenance(maintenance);
    setFormData({
      transaction_id: maintenance.transaction_id,
      maintenance_type: maintenance.maintenance_type,
      content: maintenance.content,
      maintenance_date: maintenance.maintenance_date,
      cost: maintenance.cost,
      status: maintenance.status,
      applicant_name: maintenance.applicant_name || '',
      applicant_phone: maintenance.applicant_phone || '',
      manager: maintenance.manager || '',
      reception_type: maintenance.reception_type || '전화',
      memo: maintenance.memo || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      transactionAPI.deleteMaintenance(id).then(() => {
        queryClient.invalidateQueries(['maintenance']);
        showToast('민원이 삭제되었습니다.', 'success');
      }).catch((error) => {
        showToast(error.message || '삭제 중 오류가 발생했습니다.', 'error');
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'in_progress':
        return 'status-progress';
      case 'pending':
        return 'status-waiting';
      default:
        return 'status-cancelled';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return '완료';
      case 'in_progress':
        return '처리중';
      case 'pending':
        return '접수';
      default:
        return '취소';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">민원 관리</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <Plus size={20} />
          민원 등록
        </button>
      </div>

      {/* 검색 및 필터 */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              검색
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="계약번호로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input w-full pl-10"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              민원 종류
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="select w-full"
            >
              <option value="">전체 종류</option>
              {COMPLAINT_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              상태
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="select w-full"
            >
              <option value="">전체 상태</option>
              <option value="completed">완료</option>
              <option value="in_progress">처리중</option>
              <option value="pending">접수</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary w-full"
            >
              <Plus size={20} />
              민원 등록
            </button>
          </div>
        </div>
      </div>

      {/* 민원 목록 테이블 */}
      <div className="card">
        {isLoading ? (
          <div className="loading">로딩 중...</div>
        ) : maintenance?.data?.length === 0 ? (
          <div className="empty-state">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500">민원 내역이 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>접수일</th>
                    <th>민원 종류</th>
                    <th>신청자</th>
                    <th>연락처</th>
                    <th>담당자</th>
                    <th>접수 유형</th>
                    <th>민원 내용</th>
                    <th>비용</th>
                    <th>상태</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {maintenance?.data?.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(item.maintenance_date)}</td>
                      <td className="font-medium">{item.maintenance_type}</td>
                      <td>{item.applicant_name || '-'}</td>
                      <td>{item.applicant_phone || '-'}</td>
                      <td>{item.manager || '-'}</td>
                      <td>{item.reception_type || '-'}</td>
                      <td className="max-w-xs truncate">{item.content}</td>
                      <td>₩{item.cost?.toLocaleString()}</td>
                      <td>
                        <span className={`status-badge ${getStatusColor(item.status)}`}>
                          {getStatusText(item.status)}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            {maintenance?.pagination && (
              <div className="flex justify-between items-center mt-6">
                <div className="text-sm text-gray-700">
                  총 {maintenance.pagination.total}개 중 {maintenance.pagination.from}-{maintenance.pagination.to}개
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="btn btn-secondary"
                  >
                    이전
                  </button>
                  <span className="px-3 py-2 text-sm text-gray-700">
                    {currentPage} / {maintenance.pagination.last_page}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage >= maintenance.pagination.last_page}
                    className="btn btn-secondary"
                  >
                    다음
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 민원 등록/수정 모달 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="text-xl font-bold mb-4">
              {editingMaintenance ? '민원 수정' : '민원 등록'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    계약번호 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.transaction_id}
                    onChange={(e) => setFormData({...formData, transaction_id: e.target.value})}
                    className="input w-full"
                    placeholder="계약번호를 입력하세요"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    민원 종류 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.maintenance_type}
                    onChange={(e) => setFormData({...formData, maintenance_type: e.target.value})}
                    className="select w-full"
                    required
                  >
                    {COMPLAINT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    민원 접수 유형
                  </label>
                  <select
                    value={formData.reception_type}
                    onChange={(e) => setFormData({...formData, reception_type: e.target.value})}
                    className="select w-full"
                  >
                    {RECEPTION_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    신청자 이름
                  </label>
                  <input
                    type="text"
                    value={formData.applicant_name}
                    onChange={(e) => setFormData({...formData, applicant_name: e.target.value})}
                    className="input w-full"
                    placeholder="신청자 이름을 입력하세요"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    신청자 연락처
                  </label>
                  <input
                    type="text"
                    value={formData.applicant_phone}
                    onChange={(e) => setFormData({...formData, applicant_phone: e.target.value})}
                    className="input w-full"
                    placeholder="010-0000-0000"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    담당자
                  </label>
                  <input
                    type="text"
                    value={formData.manager}
                    onChange={(e) => setFormData({...formData, manager: e.target.value})}
                    className="input w-full"
                    placeholder="담당자명을 입력하세요"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    접수일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.maintenance_date}
                    onChange={(e) => setFormData({...formData, maintenance_date: e.target.value})}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    민원 내용 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    className="input w-full"
                    rows="4"
                    placeholder="민원 내용을 입력하세요"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    비용
                  </label>
                  <NumberInput
                    value={formData.cost}
                    onChange={(val) => setFormData({ ...formData, cost: val })}
                    className="input w-full"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    상태
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="select w-full"
                    required
                  >
                    <option value="pending">접수</option>
                    <option value="in_progress">처리중</option>
                    <option value="completed">완료</option>
                    <option value="cancelled">취소</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    메모
                  </label>
                  <textarea
                    value={formData.memo}
                    onChange={(e) => setFormData({...formData, memo: e.target.value})}
                    className="input w-full"
                    rows="3"
                    placeholder="추가 메모를 입력하세요"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={mutation.isLoading}
                >
                  {mutation.isLoading ? '저장 중...' : (editingMaintenance ? '수정' : '등록')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingMaintenance(null);
                    resetForm();
                  }}
                  className="btn btn-secondary flex-1"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Maintenance; 
